import React from 'react';
import noop from '@gik/core/utils/noop';
import { useBemCN } from '@gik/core/utils/bemBlock';
import FileUtils, { AttachImageResult } from '@gik/core/utils/FileUtils';
import { Button, IImageCropData, Image, ImageCropper, UI } from '@gik/ui';
import ImageUtils from '@gik/core/utils/ImageUtils';
import { renderPortalWithRef } from '@gik/core/utils/RenderPortal';

export interface IImagePickerModalProps {
  fallback?: string;
  footerRef?: React.MutableRefObject<HTMLElement>;
  text?: string;
  onChange?: (croppedImage: string) => void;
}

export default function ImagePickerModal({
  fallback,
  footerRef,
  onChange = noop,
  text = 'Upload a photo.',
}: IImagePickerModalProps) {
  const bem = useBemCN('image-picker-modal');

  const [attachResult, setAttachResult] = React.useState<AttachImageResult>(null);
  const [image, setImage] = React.useState<IImageCropData>(null);

  async function uploadPhoto() {
    setAttachResult(await FileUtils.attachImage());
  }

  function handleChange(imageData: IImageCropData) {
    setImage(imageData);
  }

  async function save() {
    const croppedImage = await ImageUtils.getCroppedImage(image.image, image.croppedAreaPixels, image.rotation);
    onChange(croppedImage);
    UI.closeDialog();
  }

  function cancel() {
    UI.closeDialog();
  }

  const buttons = (
    <>
      <Button disabled={!image} onClick={save}>
        Save Photo
      </Button>
      <Button variant={'default'} onClick={cancel}>
        Cancel
      </Button>
    </>
  );

  // FIXME: can we use the renderPortal function here?
  return (
    <div {...bem()}>
      <div {...bem('content-wrapper')}>
        <ImageCropper
          aspect={1}
          image={attachResult}
          fallback={fallback && <Image src={fallback} />}
          onChange={handleChange}
        />
        <div {...bem('controls')}>
          <p>{text}</p>
          <Button onClick={uploadPhoto}>Upload Photo</Button>
        </div>
      </div>

      {renderPortalWithRef(buttons, footerRef)}
    </div>
  );
}

export interface IOpenImagePickerModalProps extends Omit<IImagePickerModalProps, 'footerRef'> {
  title?: string;
}

export function openImagePickerModal(props: IOpenImagePickerModalProps) {
  return UI.dialog(
    ({ footerRef }) => (
      <ImagePickerModal text={props.text} fallback={props.fallback} footerRef={footerRef} onChange={props.onChange} />
    ),
    {
      title: props.title,
    }
  );
}
